<template lang="pug">
div.px-8
  v-row(no-gutters)
    v-col.pr-8.shrink
      div.sticky.white(style="width: 230px; top: 106px;")
        .text-md.font-semibold.neutral200.px-6.py-2.round-md.mb-2 Price tier
        .text-md.font-semibold.neutral500--text.px-6.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/settings/medicine-settings')") Medicine details
        .text-md.font-semibold.neutral500--text.px-6.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/settings/supplier-settings')") Suppliers

    v-col.grow
      v-container.pt-10
        v-breadcrumbs.pt-0.px-0.pb-3(:items="breadcrumbItems")
          template(v-slot:item="{ item }")
            a.text-sm.font-medium.no-underline.neutral700--text(v-if="!item.disabled" @click="$router.push(item.href)") {{ item.text }}
            .text-sm.neutral700--text(v-else) {{ item.text }}

        .text-2xl.font-semibold.neutral700--text Inventory and services settings
        v-divider.mt-2
        .text-lg.font-medium.neutral700--text.mt-4 Price tier 
        .text-sm.neutral700--text Add, edit, remove or change status of your price tier.

        v-row.mt-6.pb-2.pb-md-0(no-gutters)
          v-col(cols="12" md="7")
            v-tabs(v-model="tabs" color="basic700" :height="44" :grow="isMobileScreen")
              v-tab.text-sm.font-semibold.text-none.px-4 {{ $t('all') }}
              v-tab.text-sm.font-semibold.text-none.px-4 Active
              v-tab.text-sm.font-semibold.text-none.px-4 Archived
          v-col(cols="12" md="5" align="end")
            .d-flex.align-center
              v-spacer
              v-btn(@click="clickSearch" icon color="neutral500")
                v-icon(size="20") $yzi-search-sm
              v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
              v-btn.button-sm.ml-4(color="neutral700" dark depressed @click="openPricingTierDialog = true") Add price tier
        v-divider

        .data-table
          v-data-table.mt-sm-4.pb-6(:headers="headers" :items="tiers" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" :disable-sort="isMobileScreen" :header-props="{ sortIcon: '$yzi-triangle' }")
            //- name
            template(v-slot:item.name="{ item }")
              div.py-2
                .text-sm.font-semibold.neutral700--text {{ item.name }}
                .text-sm.neutral700--text {{ item.description }}
            //- payment
            template(v-slot:item.payment="{ item }")
              div.d-flex.align-center.py-2
                div
                  .text-xs.neutral700--text Panel: {{ getPanelCount(item.panels) }}
                  .text-xs.neutral700--text.mt-1 Self-Pay: {{ getSelfPayCount(item.self_pay) }}
                v-btn.ml-3(icon small @click="openDetails(item)")
                  v-icon(size="20") mdi-information-outline
            //- status
            template(v-slot:item.status="{ item }")
              y-chip(v-if="item.status === 'active'" label="Active" color="green")
                template(#prepend)
                  v-icon.mr-1(size="12" color="success600") mdi-record
              y-chip(v-else label="Archived" color="red")
                template(#prepend)
                  v-icon.mr-1(size="12" color="danger600") mdi-record
            //- actions
            template(v-slot:item.actions="{ item, index }")
              div.d-flex
                v-spacer
                v-menu(offset-y :top="menuPositionTop(index)" left attach rounded="lg")
                  template(#activator="{ on }")
                    v-btn(icon small v-on="on")
                      v-icon mdi-dots-horizontal
                  v-list.py-0
                    v-list-item(@click="editTier(item)")
                      v-list-item-title.text-sm.neutral700--text Edit this tier
                    v-divider
                    v-list-item(@click="changeStatus(item)")
                      v-list-item-title.text-sm(:class="item.status === 'active' ? 'danger500--text' : 'success600--text'") {{ item.status === 'active' ? 'Archive' : 'Activate' }} this tier
                    template(v-if="tabs === 2")
                      v-divider
                      v-list-item(@click="deleteItem(item)")
                        v-list-item-title.text-sm.danger500--text Delete this tier

  .dialogs
    pricing-tier-dialog(v-if="openPricingTierDialog" :open.sync="openPricingTierDialog" :parentTier="selectedTier" @closed="closed" @reset="reset")
    pricing-tier-details-dialog(v-if="openPricingTierDetailsDialog" :open.sync="openPricingTierDetailsDialog" :tier="selectedTier" @closed="selectedTier = null")
    delete-confirmation-dialog(v-if="openDeleteDialog" :open.sync="openDeleteDialog" :loading="isLoading" @cancel="closed" @delete="confirmDelete")
      .text-lg.font-medium Are you sure you want to delete this price tier?
      .text-md.mt-4 This action will be done immediately and can’t be undone. Click “Confirm” to proceed

</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import PricingTierDialog from '@/components/clinic/inventory/PricingTierDialog.vue'
import PricingTierDetailsDialog from '@/components/clinic/inventory/PricingTierDetailsDialog.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'

export default {
  name: 'PriceTierSettings',
  components: { PricingTierDialog, PricingTierDetailsDialog, DeleteConfirmationDialog },
  data: () => ({
    isFetching: false,
    isLoading: false,
    showSearchField: false,
    openDeleteDialog: false,
    openPricingTierDetailsDialog: false,
    openPricingTierDialog: false,
    selectedTier: null,
    tabs: 0,
    pageSize: -1,
    searchText: '',
    tiers: [],
    headers: [
      { text: 'Name', value: 'name', enable: true },
      { text: 'Payment methods', value: 'payment', enable: true },
      { text: 'Status', value: 'status', enable: true },
      { text: 'Actions', value: 'actions', enable: true, sortable: false, class: 'text-end' }
    ],
    breadcrumbItems: [
      {
        text: 'Clinic management',
        disabled: false,
        href: '/admin/clinic/settings'
      },
      {
        text: 'Inventory and services settings',
        disabled: true
      }
    ]
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
  },
  watch: {
    tabs () {
      this.searchText = null
      this.fetchPricingTiers()
    },
  },
  mounted () {
    this.fetchPricingTiers()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchPricingTiers () {
      this.isFetching = true
      let params = {
        page_size: this.pageSize,
        company: this.company.id,
      }
      if (this.tabs === 1) { params.status = 'active' }
      if (this.tabs === 2) { params.status = 'archived' }
      if (this.searchText) { params.search = this.searchText }
      this.$axios.get('/product_pricetiers', { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.tiers = resp.data.results
          } else {
            this.tiers = []
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    changeStatus (item) {
      this.isFetching = true
      let newStatus = 'active'
      if (item.status === 'active') {
        newStatus = 'archived'
      }
      this.$axios.patch(`/product_pricetiers/${item.id}`, { status: newStatus })
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    confirmDelete () {
      this.isLoading = true
      this.$axios.delete(`/product_pricetiers/${this.selectedTier.id}`)
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    queryChanged: debounce(function () {
      this.fetchPricingTiers()
    }, 750),
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    deleteItem (item) {
      this.selectedTier = item
      this.openDeleteDialog = true
    },
    getPanelCount (panels) {
      const includedPanel = panels.length
      if (this.tiers.total_panels === includedPanel) {
        return 'All included'
      } else if (includedPanel === 0) {
        return 'None included'
      } else {
        return includedPanel + ' included'
      }
    },
    getSelfPayCount (methods) {
      const includedMethods = methods.length
      if (includedMethods === this.tiers.total_self_pay) {
        return 'All included'
      } else if (includedMethods === 0) {
        return 'None included'
      } else {
        return includedMethods + ' included'
      }
    },
    editTier (item) {
      this.selectedTier = item
      this.openPricingTierDialog = true
    },
    openDetails (item) {
      this.selectedTier = item
      this.openPricingTierDetailsDialog = true
    },
    reset () {
      this.fetchPricingTiers()
      this.selectedTier = null
      this.openPricingTierDialog = false
      this.openDeleteDialog = false
    },
    closed () {
      this.selectedTier = null
      this.openPricingTierDialog = false
      this.openDeleteDialog = false
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 4) {
        let start = this.tiers.length - 3
        if (position > start) {
          return true
        }
      }
      return false
    },
  }
}
</script>

<style lang="scss" scoped>
</style>